import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

export type BookingChainErrorKind = 'detachUnsupported' | 'alreadyChained' | 'validation' | 'generic';

export interface BookingChainError {
  readonly kind: BookingChainErrorKind;
  readonly messageKey: string;
  readonly details?: string;
}

interface ApiViolation {
  readonly message?: string;
}

const GENERIC: BookingChainError = { kind: 'generic', messageKey: 'booking.chain.error.generic' };

/**
 * A teszt backenden mért hibaválaszokat képezi le felhasználói üzenetkulcsokra.
 * A leképezés a válasz `detail` szövegére támaszkodik, mert a backend nem ad hibakódot.
 */
@Injectable({
  providedIn: 'root'
})
export class BookingChainErrorMapper {
  map(error: unknown): BookingChainError {
    if (!(error instanceof HttpErrorResponse)) {
      return GENERIC;
    }
    const detail = this.detailOf(error);
    if (error.status === 400 && detail.includes('previousBooking') && detail.includes('"null" given')) {
      return { kind: 'detachUnsupported', messageKey: 'booking.chain.error.detachUnsupported' };
    }
    if (error.status === 500 && detail.includes('foreign key constraint')) {
      return { kind: 'alreadyChained', messageKey: 'booking.chain.error.alreadyChained' };
    }
    const violations = this.violationsOf(error);
    if (violations.length) {
      return { kind: 'validation', messageKey: 'booking.chain.error.generic', details: violations.join(', ') };
    }
    return GENERIC;
  }

  private detailOf(error: HttpErrorResponse): string {
    const body = error.error as { detail?: string; description?: string } | null;
    return body?.detail ?? body?.description ?? '';
  }

  private violationsOf(error: HttpErrorResponse): string[] {
    const body = error.error as { violations?: ApiViolation[] } | null;
    if (!Array.isArray(body?.violations)) {
      return [];
    }
    return body.violations.map((violation) => violation.message ?? '').filter((message) => message.length > 0);
  }
}
